import React from 'react';
import { useNavigate } from 'react-router-dom';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined';
import PrivacyTipOutlined from '@mui/icons-material/PrivacyTipOutlined';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';

import Markdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { useTrackedFormik } from '@omnigenbiodata/ui';

import MainLayout from '../../../../layout/Main';
import ROUTES from '../../../../core/constants/routes.constant';
import CONTENT from '../../../../core/constants/content.constant';
import { chartColours } from '../../../../core/constants/colours.constants';
import PageContainer from '../../../../components/content/PageContainer';
import PageSection from '../../../../components/content/PageSection';

function PrivacyScene() {
  const { t } = useTranslation('privacy');
  const navigate = useNavigate();

  const formik = useTrackedFormik(
    {
      initialValues: { agree: false },
      onSubmit: () => {
        navigate(ROUTES.consentCommunication);
      },
    },
    'consent-privacy',
  );

  return (
    <MainLayout>
      <PageContainer
        title={t('title')}
        icon={
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${chartColours[3]}26`,
              color: chartColours[3],
              flexShrink: 0,
              border: 1,
              borderColor: `${chartColours[3]}55`,
            }}
          >
            <PrivacyTipOutlined sx={{ fontSize: 34 }} />
          </Box>
        }
      >
        <form onSubmit={formik.handleSubmit}>
          {[...Array(7)].map((_, i) => {
            const sectionColour = chartColours[(i + 3) % chartColours.length];
            const sectionTitle = t(`section${i + 1}.title`);

            return (
              <PageSection
                title={sectionTitle}
                colour={sectionColour}
                key={`privacy-${i}`}
                icon={<ShieldOutlined sx={{ fontSize: 24 }} />}
              >
                <Typography variant="body2" component="div" color="text.secondary">
                  <Markdown linkTarget="_blank">
                    {t(`section${i + 1}.content`, {
                      webAddress: CONTENT.MICROSITE_URL,
                      webAddressFriendly: CONTENT.MICROSITE_URL_FRIENDLY,
                      studyEmail: CONTENT.STUDY_EMAIL,
                      studyTelephone: CONTENT.STUDY_TEL,
                      termsEmail: CONTENT.TERMS_EMAIL,
                      dpoEmail: CONTENT.DPO_EMAIL,
                    })}
                  </Markdown>
                </Typography>
              </PageSection>
            );
          })}

          <Box
            mb={2.5}
            mt={2.5}
            sx={{
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'flex-end',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Button
              size="large"
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 260 },
                borderRadius: 999,
                px: 3,
                py: 1.35,
                fontWeight: 900,
                textTransform: 'none',
                boxShadow: 3,
              }}
            >
              {t('button')}
            </Button>
          </Box>

          <Accordion
            elevation={0}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: '16px !important',
              overflow: 'hidden',
              bgcolor: 'background.paper',
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel-content"
              id="panel-header"
              sx={{
                bgcolor: 'grey.50',
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiAccordionSummary-content': {
                  my: 1.5,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${chartColours[1]}26`,
                    color: chartColours[1],
                    border: 1,
                    borderColor: `${chartColours[1]}55`,
                    flexShrink: 0,
                  }}
                >
                  <HelpOutlineOutlined sx={{ fontSize: 24 }} />
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 900,
                  }}
                >
                  {t('help1.title')}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails
              id="panel-content"
              sx={{
                display: 'block',
                p: { xs: 2, md: 2.5 },
                '& p': {
                  mt: 0,
                  mb: 1.5,
                  lineHeight: 1.65,
                },
                '& p:last-child': {
                  mb: 0,
                },
                '& ul, & ol': {
                  pl: 3,
                },
                '& li': {
                  mb: 0.75,
                  lineHeight: 1.6,
                },
                '& a': {
                  fontWeight: 900,
                },
              }}
            >
              <Typography variant="body1" component="div" color="text.secondary">
                <Markdown>{t('help1.content')}</Markdown>
              </Typography>
            </AccordionDetails>
          </Accordion>
        </form>
      </PageContainer>
    </MainLayout>
  );
}

export default PrivacyScene;
